import React from 'react';
import { JcCommonProps, JcKeyFunction, JcUtils } from '../../utils/JcUtils';
import { JC_LIST_DEFAULT_ITEM_RENDERER } from '../JcList';
import { JcButtonAppearance, jcButtonAppearanceClasses } from './JcButton';
import { useJcFieldId } from '../../utils/useJcFieldId';


export interface JcDropdownProps<T> extends JcCommonProps, JcButtonAppearance {
    readonly onChange: (T: T) => void;
    readonly options: T[];
    readonly value?: T;
    readonly placeholder?: string;
    readonly itemRenderer?: (item: T) => React.ReactNode;
    readonly keyFunction: JcKeyFunction<T>;
    /** Accessible name for use outside a labelling JcField. */
    readonly ariaLabel?: string;
}


export const JcDropdown = <T,>(props: JcDropdownProps<T>): React.ReactElement => {
    const {
        onChange,
        options,
        value,
        placeholder,
        disabled,
        itemRenderer,
        keyFunction,
        ariaLabel,
        ...commonProps
    } = props;

    const id = useJcFieldId(props.id);
    const dropdownCommonProps = JcUtils.commonProps({ ...commonProps, id }, [
        "jc-dropdown",
        ...jcButtonAppearanceClasses(props, !disabled),
    ]);
    const dropdownValue = value == null ? undefined : keyFunction(value)
    const dropdownOnChange: React.ChangeEventHandler<HTMLSelectElement> = (e) => {
        const optionValue = e.target.value;
        const match = options.find(option => keyFunction(option) === optionValue)
        if (match != null) onChange(match)
    }
    const dropdownItemRenderer = (item: T): React.ReactElement => {
        const renderItem = props.itemRenderer ?? JC_LIST_DEFAULT_ITEM_RENDERER;
        return <option value={keyFunction(item)}
            key={keyFunction(item)}>
            {renderItem(item)}
        </option>
    }

    return <select {...dropdownCommonProps}
        aria-label={ariaLabel}
        disabled={disabled}
        value={dropdownValue ?? "PLACE_HOLDER"}
        onChange={dropdownOnChange}>
        {placeholder && dropdownValue == null &&
            <option value="PLACE_HOLDER" disabled>{placeholder}</option>
        }
        {options.map(dropdownItemRenderer)}
    </select>
}
